"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Mail, Phone, MapPin, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetInTouch() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="get-in-touch" className="py-20 bg-light-background">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          {}
          <div className="space-y-8">
            <div>
              <h2 className="font-display text-4xl font-bold text-foreground-light mb-4">
                Get In Touch
              </h2>
              <p className="text-foreground-light/70 text-lg">Have questions or need assistance? Reach out to the Malta Dental Specialists Association.</p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-md shrink-0">
                  <Mail className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-foreground-light mb-1">
                    Email
                  </h3>
                  <p className="text-foreground-light/70">malta.dental.specialists@gmail.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-md shrink-0">
                  <Phone className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-foreground-light mb-1">
                    Phone
                  </h3>
                  <p className="text-foreground-light/70">
                    +356 2545 4833
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-md shrink-0">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-foreground-light mb-1">
                    Location
                  </h3>
                  <p className="text-foreground-light/70">
                    Dental Department, Mater Dei Hospital, Malta
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              
            </div>
          </div>

          {}
          <div ref={ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <FormProvider className="bg-muted-light-background p-8 rounded-lg shadow-soft" formId="get_in_touch_form_home" sectionName="get-in-touch" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: false
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you for contacting us! We'll get back to you soon." errorMessage="Failed to send your message. Please try again.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-12">
        <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="size-8 text-primary" />
        </div>
        <h3 className="font-display text-2xl font-semibold text-foreground-light mb-2">
          Message Sent!
        </h3>
        <p className="text-foreground-light/70 mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormDescription>Enter your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane.doe@example.com" className="focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormDescription>We'll use this to reply to your inquiry.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+356 99 123456" className="focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormDescription>Optional - include if you prefer a phone call.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="How can we help you?" className="focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-[var(--duration-medium)] min-h-32" {...field} />
              </FormControl>
              <FormDescription>Provide details of your inquiry or comment.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-accent/10 border border-accent/20 rounded-md">
            <p className="text-accent text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-[1.03] hover:shadow-medium">
          {isSubmitting ? <>
              <Loader className="animate-spin size-4" />
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}